import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DeletePurchaseBillDialog from "@/components/purchase/DeletePurchaseBillDialog";
import MasterDedupTab from "@/components/dedup/MasterDedupTab";
import { ShieldAlert, ChevronRight, ChevronDown, Trash2 } from "lucide-react";

const DELETE_ROLES = ["super_admin", "accounts", "management"];

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? String(s).slice(0, 10) : "—");

function Stat({ label, value, tone = "stone" }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm">
      <div className={`font-display text-xl font-bold ${tone === "rose" ? "text-rose-600" : "text-stone-900"}`}>{value}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
    </div>
  );
}

function RecordTable({ records, kind, canDelete, onDeleted }) {
  if (!records?.length) return null;
  const isBill = kind === "purchase" || kind === "sales";
  const showDelete = kind === "purchase" && canDelete;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wide text-stone-500">
          <tr>
            <th className="px-3 py-2">Role</th><th className="px-3 py-2">Record ID</th>
            <th className="px-3 py-2">{isBill ? "Invoice / Doc" : "Name"}</th>
            <th className="px-3 py-2">GSTIN</th>
            {kind === "sales" && <><th className="px-3 py-2">Buyer Rec</th><th className="px-3 py-2">FY</th><th className="px-3 py-2">Source / Batch</th></>}
            <th className="px-3 py-2 text-right">Linked Txns</th>
            <th className="px-3 py-2 text-right">Value</th>
            <th className="px-3 py-2">Created</th><th className="px-3 py-2">Last Activity</th>
            {isBill && <th className="px-3 py-2">Downstream</th>}
            {showDelete && <th className="px-3 py-2 text-right">Action</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {records.map((r, i) => (
            <tr key={i} className="hover:bg-stone-50/60">
              <td className="px-3 py-2">
                {r.role === "canonical" ? <Badge className="border-0 bg-emerald-100 text-emerald-700 text-[10px]">canonical</Badge>
                  : r.role === "duplicate" ? <Badge className="border-0 bg-rose-100 text-rose-700 text-[10px]">duplicate</Badge>
                  : r.recommended ? <Badge className="border-0 bg-emerald-100 text-emerald-700 text-[10px]">survivor</Badge>
                  : <Badge className="border-0 bg-stone-100 text-stone-500 text-[10px]">candidate</Badge>}
              </td>
              <td className="px-3 py-2 font-mono text-[10px] text-stone-500">{r.billId || r.id || "—"}</td>
              <td className="px-3 py-2 font-medium text-stone-800">{r.invoiceNumber || r.name || "—"}</td>
              <td className="px-3 py-2 font-mono text-[10px]">{r.gstin || r.buyerGstin || "—"}</td>
              {kind === "sales" && <>
                <td className="px-3 py-2">{r.buyerRecordName || r.buyerName || "—"}</td>
                <td className="px-3 py-2">{r.financialYear || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.source || ""}{r.importBatch ? ` · ${r.importBatch}` : ""}</td>
              </>}
              <td className="px-3 py-2 text-right tabular-nums">{r.linkedTransactions ?? (r.linkedPayment || r.linkedDebitNote || r.linkedGRN ? "yes" : "0")}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(r.transactionValue ?? r.amount)}</td>
              <td className="px-3 py-2">{dt(r.createdAt)}</td>
              <td className="px-3 py-2">{dt(r.lastActivity)}</td>
              {isBill && (
                <td className="px-3 py-2 text-[10px]">
                  {[r.linkedPayment && "payment", r.linkedDebitNote && "debit-note", r.linkedGRN && "GRN", r.stockReceived && "stock"].filter(Boolean).join(", ") || "—"}
                  {r.safeToDelete === false && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-700">review</span>}
                  {r.safeToDelete === true && <span className="ml-1 rounded bg-emerald-100 px-1 text-emerald-700">unused</span>}
                </td>
              )}
              {showDelete && (
                <td className="px-3 py-2 text-right">
                  <DeletePurchaseBillDialog
                    billId={r.billId}
                    invoiceNumber={r.invoiceNumber}
                    supplierName={r.supplierName}
                    onDeleted={onDeleted}
                    trigger={<Button size="sm" variant="outline" data-testid={`da-del-${r.billId}`} className="h-7 gap-1 border-rose-200 px-2 text-[11px] text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /> Delete</Button>}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupCard({ group, kind, canDelete, onDeleted }) {
  const [open, setOpen] = useState(false);
  const title = group.name || group.invoiceNumber || group.supplierName || "Group";
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
      <button className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-stone-50/60" onClick={() => setOpen(!open)}>
        <div>
          <div className="font-semibold text-stone-800">{title}
            {group.gstin && <span className="ml-2 font-mono text-[10px] text-stone-400">{group.gstin}</span>}
            {group.parentCompany && <span className="ml-2 text-[10px] text-stone-400">· {group.parentCompany}</span>}
          </div>
          <div className="mt-0.5 text-xs text-stone-500">{group.reason} · {group.count} records
            {group.totalValue != null && <span> · total {money(group.totalValue)}</span>}
            {group.duplicateValue != null && <span className="text-rose-600"> · duplicate value {money(group.duplicateValue)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {group.recommendedSurvivor && <Badge className="border-0 bg-emerald-100 text-emerald-700 text-[10px]">keep: {group.recommendedSurvivor}</Badge>}
          {open ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-stone-100 px-2 py-2">
          <RecordTable records={group.records} kind={kind} canDelete={canDelete} onDeleted={onDeleted} />
        </div>
      )}
    </div>
  );
}

function EntityTab({ endpoint, kind, entityLabel }) {
  const { data, isLoading, refetch } = useResource(endpoint);
  const { role } = useAuth();
  const canDelete = kind === "purchase" && DELETE_ROLES.includes(role);
  const d = data || {};
  if (isLoading) return <LoadingState label={`Scanning ${entityLabel}…`} />;
  // sales bills have 3 sections
  if (kind === "sales") {
    const hard = d.hardDuplicates || [];
    const soft = d.softDuplicates || [];
    const raw = d.allInvoiceNumbersWithMultipleRecords || [];
    const empty = !hard.length && !soft.length && !raw.length;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Hard Duplicates (same buyer)" value={d.totalHard ?? 0} tone={d.totalHard ? "rose" : "stone"} />
          <Stat label="Suspect Duplicate Buyer" value={d.totalSoft ?? 0} tone={d.totalSoft ? "rose" : "stone"} />
          <Stat label="Invoice #s w/ multiple records" value={raw.length} />
        </div>
        {empty ? <EmptyState title="No duplicate sales invoices" message="No invoice number appears more than once." /> : (
          <>
            {hard.length > 0 && <div><p className="mb-2 text-xs font-semibold uppercase text-rose-600">Hard duplicates — same buyer + FY + invoice no</p><div className="space-y-2">{hard.map((g, i) => <GroupCard key={i} group={g} kind="sales" />)}</div></div>}
            {soft.length > 0 && <div><p className="mb-2 text-xs font-semibold uppercase text-amber-600">Suspect duplicate buyer — same GSTIN + FY + invoice no under different buyer records</p><div className="space-y-2">{soft.map((g, i) => <GroupCard key={i} group={g} kind="sales" />)}</div></div>}
            {raw.length > 0 && !hard.length && !soft.length && <div className="space-y-2">{raw.map((g, i) => <GroupCard key={i} group={{ ...g, reason: "Same invoice number", recommendedSurvivor: null }} kind="sales" />)}</div>}
          </>
        )}
      </div>
    );
  }
  const groups = d.groups || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Duplicate groups" value={d.totalGroups ?? 0} tone={d.totalGroups ? "rose" : "stone"} />
        <Stat label="Duplicate records" value={d.totalDuplicateRecords ?? 0} tone={d.totalDuplicateRecords ? "rose" : "stone"} />
        {kind === "purchase" && <Stat label="Duplicate value" value={money(d.totalDuplicateValue ?? 0)} tone={d.totalDuplicateValue ? "rose" : "stone"} />}
      </div>
      {groups.length === 0 ? <EmptyState title={`No duplicate ${entityLabel}`} message="No suspected duplicates found by the current rules." /> : (
        <div className="space-y-2">{groups.map((g, i) => <GroupCard key={i} group={g} kind={kind} canDelete={canDelete} onDeleted={refetch} />)}</div>
      )}
    </div>
  );
}

const TABS = [
  { key: "suppliers", label: "Suppliers", endpoint: "/data-integrity/supplier-duplicates", kind: "master", entityLabel: "suppliers" },
  { key: "buyers", label: "Buyers", endpoint: "/data-integrity/buyer-duplicates", kind: "master", entityLabel: "buyers" },
  { key: "ingredients", label: "Ingredients", endpoint: "/data-integrity/ingredient-duplicates", kind: "master", entityLabel: "ingredients" },
  { key: "products", label: "Products", endpoint: "/data-integrity/product-duplicates", kind: "master", entityLabel: "products" },
  { key: "sales", label: "Sales Bills", endpoint: "/data-integrity/sales-bill-duplicates", kind: "sales", entityLabel: "sales bills" },
  { key: "purchase", label: "Purchase Bills", endpoint: "/data-integrity/purchase-bill-duplicates", kind: "purchase", entityLabel: "purchase bills" },
];

export default function DuplicateAudit() {
  const [tab, setTab] = useState("suppliers");
  const active = TABS.find((t) => t.key === tab);
  return (
    <div className="space-y-6">
      <PageHeader title="Duplicate Audit" subtitle="Read-only scan of production data for duplicate masters & invoices" />
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="da-readonly-banner">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div><b>Mostly read-only.</b> This screen <i>detects</i> suspected duplicates. On the <b>Purchase Bills</b> tab, authorized users (Super Admin, Accounts, Management) can delete a duplicate bill with safe, idempotent inventory reversal — blocked if the received stock was already consumed. All other entities are detect-only.</div>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList data-testid="duplicate-audit-tabs" className="flex-wrap bg-stone-100">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`da-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key}>
            {t.kind === "master"
              ? <MasterDedupTab entity={t.key} entityLabel={t.entityLabel} />
              : <EntityTab endpoint={t.endpoint} kind={t.kind} entityLabel={t.entityLabel} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
